import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { DashboardLayout } from '../components/layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Award,
    Medal,
    Download,
    Calendar,
    BookOpen,
    TrendingUp,
    Target
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const badgeInfo = {
    basic_complete: { icon: BookOpen, color: 'bg-emerald-100 text-emerald-700', label_id: 'Basic Selesai', label_en: 'Basic Complete' },
    intermediate_complete: { icon: TrendingUp, color: 'bg-blue-100 text-blue-700', label_id: 'Intermediate Selesai', label_en: 'Intermediate Complete' },
    advanced_complete: { icon: Target, color: 'bg-purple-100 text-purple-700', label_id: 'Advanced Selesai', label_en: 'Advanced Complete' },
    expert_complete: { icon: Award, color: 'bg-amber-100 text-amber-700', label_id: 'Expert Selesai', label_en: 'Expert Complete' }
};

export const CertificatePage = () => {
    const { t, language } = useLanguage();
    const [certificates, setCertificates] = useState([]);
    const [badges, setBadges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('badges');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [certRes, badgeRes] = await Promise.all([
                axios.get(`${API}/certificates`),
                axios.get(`${API}/badges`)
            ]);
            setCertificates(certRes.data);
            setBadges(badgeRes.data);
        } catch (error) {
            console.error('Error fetching certificates:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map((i) => (
                            <Skeleton key={i} className="h-32" />
                        ))}
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6 animate-fade-in">
                {/* Header */}
                <div>
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-slate-900">
                        {t('certificate')}
                    </h1>
                    <p className="text-slate-500 mt-1">
                        {language === 'id' 
                            ? 'Badge dan sertifikat yang telah Anda peroleh'
                            : 'Your earned badges and certificates'
                        }
                    </p>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="badges" data-testid="tab-badges">
                            {t('myBadges')} ({badges.length})
                        </TabsTrigger>
                        <TabsTrigger value="certificates" data-testid="tab-certificates">
                            {t('myCertificates')} ({certificates.length})
                        </TabsTrigger>
                    </TabsList>

                    {/* Badges Tab */}
                    <TabsContent value="badges" className="mt-6">
                        {badges.length === 0 ? (
                            <Card>
                                <CardContent className="py-12 text-center">
                                    <Medal className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                                    <h3 className="text-lg font-heading font-semibold text-slate-700">
                                        {language === 'id' ? 'Belum ada badge' : 'No badges yet'}
                                    </h3>
                                    <p className="text-slate-500 mt-2">
                                        {language === 'id' 
                                            ? 'Selesaikan level untuk mendapatkan badge'
                                            : 'Complete levels to earn badges'
                                        }
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {badges.map((badge) => {
                                    const info = badgeInfo[badge.badge_type];
                                    const Icon = info?.icon || Award;

                                    return (
                                        <Card 
                                            key={badge.id} 
                                            className="text-center"
                                            data-testid={`badge-${badge.badge_type}`}
                                        >
                                            <CardContent className="pt-6">
                                                <div className={`w-16 h-16 rounded-2xl mx-auto flex items-center justify-center ${info?.color || 'bg-slate-100'}`}>
                                                    <Icon className="w-8 h-8" />
                                                </div>
                                                <h3 className="font-heading font-semibold text-slate-900 mt-4">
                                                    {language === 'id' ? info?.label_id : info?.label_en}
                                                </h3>
                                                <p className="text-xs text-slate-400 mt-2 flex items-center justify-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {formatDate(badge.earned_at)}
                                                </p>
                                            </CardContent>
                                        </Card>
                                    );
                                })}

                                {/* Placeholder for remaining badges */}
                                {Object.entries(badgeInfo)
                                    .filter(([key]) => !badges.some(b => b.badge_type === key))
                                    .map(([key, info]) => {
                                        const Icon = info.icon;
                                        return (
                                            <Card 
                                                key={key} 
                                                className="text-center opacity-40"
                                                data-testid={`badge-locked-${key}`}
                                            >
                                                <CardContent className="pt-6">
                                                    <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center bg-slate-100">
                                                        <Icon className="w-8 h-8 text-slate-400" />
                                                    </div>
                                                    <h3 className="font-heading font-semibold text-slate-400 mt-4">
                                                        {language === 'id' ? info.label_id : info.label_en}
                                                    </h3>
                                                    <p className="text-xs text-slate-300 mt-2">
                                                        {t('locked')}
                                                    </p>
                                                </CardContent>
                                            </Card>
                                        );
                                    })
                                }
                            </div>
                        )}
                    </TabsContent>

                    {/* Certificates Tab */}
                    <TabsContent value="certificates" className="mt-6">
                        {certificates.length === 0 ? (
                            <Card>
                                <CardContent className="py-12 text-center">
                                    <Award className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                                    <h3 className="text-lg font-heading font-semibold text-slate-700">
                                        {t('noCertificates')}
                                    </h3>
                                    <p className="text-slate-500 mt-2">
                                        {t('completeTo')}
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {certificates.map((cert) => (
                                    <Card 
                                        key={cert.id} 
                                        className="overflow-hidden"
                                        data-testid={`certificate-${cert.id}`}
                                    >
                                        {/* Certificate Preview */}
                                        <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-8 text-center text-white">
                                            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                                                <Award className="w-8 h-8 text-primary" />
                                            </div>
                                            <p className="text-sm text-slate-400 uppercase tracking-wider">
                                                Certificate of Completion
                                            </p>
                                            <h3 className="text-2xl font-heading font-bold mt-2">
                                                NUSATRADE
                                            </h3>
                                            <p className="text-lg text-slate-300 mt-2">
                                                {cert.user_name}
                                            </p>
                                            <p className="text-sm text-slate-400 mt-4">
                                                {language === 'id' 
                                                    ? 'Telah menyelesaikan level Expert'
                                                    : 'Has completed Expert Level'
                                                }
                                            </p>
                                        </div>
                                        <CardContent className="pt-4">
                                            <div className="flex items-center justify-between text-sm">
                                                <div>
                                                    <p className="text-slate-400">{t('certificateNumber')}</p>
                                                    <p className="font-mono font-medium text-slate-700">{cert.certificate_number}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-slate-400">{t('issuedOn')}</p>
                                                    <p className="font-medium text-slate-700">{formatDate(cert.issued_at)}</p>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
};
